// ============================================================
// Quotas annuels d'un opérateur par financement et par territoire (CE-1369,
// CE-1418 : un quota vaut pour l'accompagnement total et partiel ensemble) :
// lecture des quotas d'une année, des mesures utilisées (CE-1370) et de leur
// part par accompagnement, totaux, état d'une consommation (anneau), saisie
// d'un financement dans le panneau latéral, corps de la requête (l'année
// entière, puisque l'API la remplace), alerte « aucun site ne dessert ce
// territoire ». La saisie d'un financement est { [territoire]: "…" } ; les
// financements viennent de l'API. Fonctions pures, testées dans
// quotasOperateur.test.js.
// ============================================================

/** Les quatre territoires de la Caf, dans l'ordre que rend l'API. */
export const TERRITOIRES = [
  { id: "NORD", libelle: "Nord" },
  { id: "SUD", libelle: "Sud" },
  { id: "EST", libelle: "Est" },
  { id: "OUEST", libelle: "Ouest" },
];

export const MESSAGE_QUOTA_INVALIDE = "Saisissez un nombre entier de mesures, par exemple 40, ou laissez vide.";

/** Une saisie vide : pas de quota (`nombre` null). Sinon un entier positif ou nul, espaces de milliers admis. */
export function lireQuota(saisie) {
  const propre = String(saisie == null ? "" : saisie).replace(/\s/g, "");
  if (propre === "") return { valide: true, nombre: null };
  if (!/^\d+$/.test(propre)) return { valide: false, message: MESSAGE_QUOTA_INVALIDE };
  return { valide: true, nombre: parseInt(propre, 10) };
}

/** L'identifiant du champ d'un territoire dans le panneau, qui sert aussi de clé à son erreur. */
export const idChamp = (territoire) => `quota-${territoire}`;

const quotasDe = (annee) => (annee && annee.quotas) || [];

const retient = (q, { financement = null, territoire = null } = {}) =>
  (!financement || q.financement === financement) && (!territoire || q.territoire === territoire);

/** Le quota enregistré d'un financement et d'un territoire ; null sans quota. */
export function quotaDe(annee, financement, territoire) {
  const quota = quotasDe(annee).find((q) => retient(q, { financement, territoire }));
  return quota ? quota.nombre : null;
}

/** Le total des quotas enregistrés d'une année, en tout ou filtré par financement et territoire. */
export function totalQuotas(annee, filtre = {}) {
  return quotasDe(annee).filter((q) => retient(q, filtre)).reduce((total, q) => total + q.nombre, 0);
}

/** Les mesures déjà décomptées sur un quota (CE-1370) ; zéro quand l'API ne les porte pas ou sans quota. */
export function utiliseesDe(annee, financement, territoire) {
  const quota = quotasDe(annee).find((q) => retient(q, { financement, territoire }));
  return (quota && quota.utilisees) || 0;
}

/** Le total des mesures décomptées d'une année, en tout ou filtré comme `totalQuotas`. */
export function totalUtilisees(annee, filtre = {}) {
  return quotasDe(annee).filter((q) => retient(q, filtre)).reduce((total, q) => total + (q.utilisees || 0), 0);
}

/** La part des mesures décomptées en accompagnement total et en partiel, filtrée comme `totalQuotas` ; zéro quand l'API ne la porte pas. */
export function partsUtilisees(annee, filtre = {}) {
  return quotasDe(annee)
    .filter((q) => retient(q, filtre))
    .reduce(
      (parts, q) => {
        const parRegime = q.utiliseesParRegime || {};
        return { total: parts.total + (parRegime.TOTAL || 0), partiel: parts.partiel + (parRegime.PARTIEL || 0) };
      },
      { total: 0, partiel: 0 }
    );
}

/** « dont 9 en total, 3 en partiel », seulement quand des mesures sont utilisées : sinon des zéros dans chaque case. */
export function libellePart({ total, partiel }) {
  return total + partiel > 0 ? `dont ${total} en total, ${partiel} en partiel` : null;
}

const SEUIL_ALERTE = 80;

/**
 * L'état d'une consommation : le taux de remplissage de l'anneau, son niveau (vide sans quota, normal,
 * proche à partir de 80 %, atteint quand le quota est consommé ou dépassé) et ce qu'il reste, pour
 * l'infobulle et le libellé accessible. Un quota saisi à zéro n'est pas « aucun quota ».
 */
export function consommation(utilisees, quota) {
  const u = utilisees || 0;
  const sansQuota = quota === null || quota === undefined;
  const q = sansQuota ? 0 : quota;
  const reste = Math.max(q - u, 0);
  if (sansQuota && u === 0) return { utilisees: 0, quota: 0, taux: 0, niveau: "vide", libelle: "Aucun quota" };
  const atteint = q > 0 ? u >= q : u > 0;
  const taux = q > 0 ? Math.min(100, Math.round((u / q) * 100)) : u > 0 ? 100 : 0;
  if (atteint) return { utilisees: u, quota: q, taux, niveau: "atteint", libelle: `Quota atteint${u > q ? ` et dépassé de ${u - q}` : ""}` };
  return { utilisees: u, quota: q, taux, niveau: taux >= SEUIL_ALERTE ? "proche" : "normal", libelle: `${reste} mesure${reste > 1 ? "s" : ""} restante${reste > 1 ? "s" : ""}` };
}

/** Les financements qui ont une colonne : ceux dont l'année a au moins un quota enregistré (zéro compris), dans l'ordre de l'API. */
export function financementsAffiches(financements, annee) {
  return financements.filter((f) => quotasDe(annee).some((q) => q.financement === f.id));
}

/** La saisie d'un financement : un champ par territoire, rempli des quotas enregistrés, vide sans quota. */
export function saisieDepuisAnnee(annee, financement) {
  return TERRITOIRES.reduce((saisie, t) => {
    const nombre = quotaDe(annee, financement, t.id);
    return { ...saisie, [t.id]: nombre === null ? "" : String(nombre) };
  }, {});
}

/** Le total des saisies valides du panneau, en tout ou pour un territoire : une saisie en erreur ne compte pas. */
export function totalSaisie(saisie, { territoire = null } = {}) {
  return TERRITOIRES.filter((t) => !territoire || t.id === territoire).reduce((total, t) => total + (lireQuota(saisie[t.id]).nombre || 0), 0);
}

/** Les quotas du financement saisis dans le panneau (par territoire, cases vides exclues) et les erreurs par champ. */
export function validerSaisie(saisie, financement) {
  const erreurs = {};
  const quotas = [];
  TERRITOIRES.forEach((t) => {
    const lu = lireQuota(saisie[t.id]);
    if (!lu.valide) erreurs[idChamp(t.id)] = lu.message;
    else if (lu.nombre !== null) quotas.push({ financement, territoire: t.id, nombre: lu.nombre });
  });
  return { erreurs, quotas };
}

/** Le corps de la requête : l'API remplace l'année, on lui renvoie les quotas des autres financements tels quels, plus ceux du panneau. */
export function corpsRequete(annee, financement, quotasDuFinancement) {
  return [
    ...quotasDe(annee)
      .filter((q) => q.financement !== financement)
      .map((q) => ({ financement: q.financement, territoire: q.territoire, nombre: q.nombre })),
    ...quotasDuFinancement,
  ];
}

/** Des quotas sur un territoire qu'aucun site de l'opérateur ne dessert ne pourront pas être réalisés. */
export function territoireSansSite(couverture, nombre) {
  return Number(nombre) > 0 && !(couverture && couverture.communes && couverture.communes.length);
}

export function formaterMesures(nombre) {
  return `${Number(nombre).toLocaleString("fr-FR")} mesure${nombre > 1 ? "s" : ""}`;
}

/** « 2 communes sur 3 · FTM Saint-Denis » : les communes desservies sur celles du territoire, et les sites. */
export function resumeCouverture(couverture) {
  const nombre = couverture.communes.length;
  const sites = couverture.sites.map((s) => s.nom).join(", ");
  return `${nombre} commune${nombre > 1 ? "s" : ""} sur ${couverture.nombreCommunesTerritoire} · ${sites}`;
}
